// Accessibility behaviour for the app's modals.
//
// Every modal is a `.modal-overlay` wrapping a `.modal-content`, opened by
// adding the `active` class and closed by removing it — 43 call sites spread
// across js/app.js and js/ui/*.js, with no central helper (see js/ui/navigation.js
// ~line 121 for the most common close path, the delegated `.btn-close-modal`
// click handler). This module does not touch any of those call sites: it
// watches for the `active` class itself via a MutationObserver, so it works
// uniformly for every existing modal and any added later.
//
// What it does:
//   - moves focus into a newly-opened modal (preferring a field to fill in or
//     the primary action), and returns focus to whatever had it before once
//     the modal closes
//   - traps Tab / Shift+Tab within the topmost open modal
//
// What it deliberately does NOT do:
//   - close modals on Escape — js/ui/nav-hotkeys.js (~line 263) and a few
//     modal-specific listeners (contact.js, privacy-policy.js, welcome-editor.js,
//     dialog.js) already own that per modal; adding a second handler here would
//     race them
//   - manage focus for #app-dialog — that is window.Dialog's own root
//     (js/ui/dialog.js), which already moves focus into its own input and
//     handles its own Escape/close. We exclude it by id everywhere below.

const DIALOG_ROOT_ID = 'app-dialog';

const FOCUSABLE_SELECTOR = [
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');

function isCloseButton(el) {
    return !!(el && el.classList && el.classList.contains('btn-close-modal'));
}

function isPreferredInitialTarget(el) {
    if (!el) return false;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'select' || tag === 'textarea') return true;
    return !!(el.classList && el.classList.contains('btn-primary'));
}

function isVisible(el) {
    if (!el || el.isConnected === false) return false;
    if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
    if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
        const style = window.getComputedStyle(el);
        if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
    }
    return true;
}

// Focusable descendants of a `.modal-content`, in DOM order, filtered to
// those actually visible. Exported (along with pickInitialFocus and
// nextTabStop below) so the pure logic can be unit-tested against plain
// fake-DOM objects without a real document — see tests/modal-a11y.test.js.
export function getFocusable(modalContent) {
    if (!modalContent || typeof modalContent.querySelectorAll !== 'function') return [];
    return Array.from(modalContent.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
}

// Chooses where focus should land when a modal opens: prefers an input,
// select, textarea, or primary button (in DOM order); falls back to the close
// button; falls back to modal-content itself, given tabindex="-1" so it can
// take programmatic focus without joining the normal Tab order.
export function pickInitialFocus(modalContent) {
    if (!modalContent) return null;

    const focusable = getFocusable(modalContent);
    const closeBtn = focusable.find(isCloseButton);
    const candidates = focusable.filter(el => el !== closeBtn);

    const preferred = candidates.find(isPreferredInitialTarget);
    if (preferred) return preferred;
    if (closeBtn) return closeBtn;

    if (typeof modalContent.hasAttribute === 'function' && typeof modalContent.setAttribute === 'function') {
        if (!modalContent.hasAttribute('tabindex')) modalContent.setAttribute('tabindex', '-1');
    }
    return modalContent;
}

// Given the ordered list of focusable elements in the topmost modal, the
// currently-focused element, and whether Shift is held, returns the element
// that Tab / Shift+Tab should move focus to next, wrapping at either end. If
// `current` isn't in the list (focus is outside the modal, or nothing is
// focused yet), lands on the first stop in the direction of travel.
export function nextTabStop(focusables, current, shiftKey) {
    if (!Array.isArray(focusables) || focusables.length === 0) return null;

    const idx = focusables.indexOf(current);
    if (shiftKey) {
        if (idx <= 0) return focusables[focusables.length - 1];
        return focusables[idx - 1];
    }
    if (idx === -1 || idx === focusables.length - 1) return focusables[0];
    return focusables[idx + 1];
}

function isDialogRoot(overlay) {
    return !!overlay && overlay.id === DIALOG_ROOT_ID;
}

function zIndexOf(el) {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return 0;
    const z = parseInt(window.getComputedStyle(el).zIndex, 10);
    return Number.isNaN(z) ? 0 : z;
}

function getTopmostActiveOverlay() {
    const overlays = Array.from(document.querySelectorAll('.modal-overlay.active')).filter(el => !isDialogRoot(el));
    if (overlays.length === 0) return null;

    // Highest z-index wins; later markup wins a tie, matching how the app's
    // own overlays stack (later-opened modals are appended active on top).
    return overlays.reduce((top, el) => (zIndexOf(el) >= zIndexOf(top) ? el : top));
}

// Remembers, per overlay, what had focus right before it opened — so a modal
// opened from inside another modal, or from a background element, restores
// focus correctly rather than to some other modal's memory.
const previousFocus = new WeakMap();

// Picks a target inside the modal and focuses it, looking again after a paint
// if the first pick came up empty.
//
// WHY THE RETRY. The overlay opens with `transition: all 0.3s` (css/main.css,
// .modal-overlay) taking visibility from hidden to visible. On the very frame
// the `active` class lands, computed visibility on everything inside is still
// `hidden` — the transition has not advanced past its first instant — so the
// first pick sees no focusable element at all and falls back to the container.
// Two animation frames later the transition is under way and the same pick
// finds the field it should have found. Focus is left alone on the retry if it
// already landed inside the modal (the user may have clicked a field by then).
function focusInto(content, attempt = 0) {
    const target = pickInitialFocus(content);
    if (target && typeof target.focus === 'function') target.focus();

    const landedOnField = document.activeElement === target && target !== content;
    if (landedOnField || attempt >= 3) return;
    if (typeof requestAnimationFrame !== 'function') return;

    requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!content.isConnected || !content.closest('.modal-overlay.active')) return;
        const active = document.activeElement;
        if (active && active !== content && content.contains(active)) return;
        focusInto(content, attempt + 1);
    }));
}

function onOverlayOpened(overlay) {
    if (isDialogRoot(overlay)) return;

    previousFocus.set(overlay, document.activeElement);
    const content = overlay.querySelector('.modal-content');
    if (!content) return;
    focusInto(content);
}

function onOverlayClosed(overlay) {
    if (isDialogRoot(overlay)) return;

    const restore = previousFocus.get(overlay);
    previousFocus.delete(overlay);
    if (restore && isVisible(restore) && typeof restore.focus === 'function') {
        restore.focus();
    }
}

function onKeydown(e) {
    if (e.key !== 'Tab') return;

    const overlay = getTopmostActiveOverlay();
    if (!overlay) return;
    const content = overlay.querySelector('.modal-content');
    if (!content) return;

    const focusable = getFocusable(content);
    if (focusable.length === 0) return;

    const next = nextTabStop(focusable, document.activeElement, e.shiftKey);
    if (next) {
        e.preventDefault();
        next.focus();
    }
}

const trackedOverlays = new WeakSet();

function trackOverlay(overlay) {
    if (!overlay || trackedOverlays.has(overlay)) return;
    trackedOverlays.add(overlay);
    // Covers a modal that is somehow already open when we start observing it
    // (e.g. added to the DOM pre-activated).
    if (overlay.classList.contains('active')) onOverlayOpened(overlay);
}

function handleMutations(mutations) {
    for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
            const overlay = mutation.target;
            if (!overlay.classList || !overlay.classList.contains('modal-overlay')) continue;
            const wasActive = (mutation.oldValue || '').split(/\s+/).includes('active');
            const isActive = overlay.classList.contains('active');
            if (isActive && !wasActive) onOverlayOpened(overlay);
            else if (!isActive && wasActive) onOverlayClosed(overlay);
        } else if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
                if (!(node instanceof Element)) return;
                if (node.matches && node.matches('.modal-overlay')) trackOverlay(node);
                if (typeof node.querySelectorAll === 'function') {
                    node.querySelectorAll('.modal-overlay').forEach(trackOverlay);
                }
            });
        }
    }
}

// Wires up modal focus handling for the whole document. Idempotent-ish: call
// once at startup (see js/app.js, next to initNavigation()). Safe to import
// outside a browser (e.g. accidentally from a test) — it no-ops without
// `document`/`MutationObserver`.
export function initModalA11y() {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;

    document.querySelectorAll('.modal-overlay').forEach(trackOverlay);

    const observer = new MutationObserver(handleMutations);
    observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true
    });

    document.addEventListener('keydown', onKeydown);

    return observer;
}
